import { Ace } from 'ace-builds'
import React, { useEffect, useState } from 'react'
import AceEditor from 'shared/components/code-editor/components/ace-editor'
import { ignoredAnnotations } from 'shared/components/code-editor/constants/ignored-annotations'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'

interface CodeEditorProps {
  value?: string | null
  label?: string | JSX.Element
  statusIcon?: React.ReactNode
  isSuccess?: boolean
  error?: string
  disabled?: boolean
  onChange: (value: string) => void
  onBlur?: (value: string) => void
  setIsValid?: (value: boolean) => void
}

const CodeEditor = ({
  value,
  label,
  statusIcon,
  isSuccess,
  error,
  disabled,
  onChange,
  onBlur,
  setIsValid,
}: CodeEditorProps) => {
  const { t } = useLocoTranslation()
  const [annotations, setAnnotations] = useState<Ace.Annotation[]>([])

  const isValid = value === '' || annotations.length === 0

  useEffect(() => {
    if (setIsValid) setIsValid(isValid)
  }, [isValid, setIsValid])

  return (
    <div className={`flex w-full flex-col gap-1`}>
      {label && label}
      <div
        className={twMerge(
          'main-transition-colors relative border',
          isValid ? 'border-transparent' : 'border-danger',
          error && 'border-danger',
          isSuccess && 'border-green',
        )}
      >
        {statusIcon && (
          <div className="bg-background-secondary absolute right-2 top-2 z-20 rounded-lg p-2 shadow-lg">
            {statusIcon}
          </div>
        )}

        <AceEditor
          mode="html"
          width="100%"
          theme="textmate"
          fontSize={15}
          setOptions={{
            enableBasicAutocompletion: true,
            enableLiveAutocompletion: true,
            enableSnippets: true,
            showPrintMargin: false,
            showGutter: true,
          }}
          annotations={annotations}
          onValidate={annotations =>
            setAnnotations(
              annotations.filter(
                annotation => !ignoredAnnotations.some(ignore => annotation.text.includes(ignore)),
              ),
            )
          }
          value={value || ''}
          highlightActiveLine={true}
          onChange={value => !disabled && onChange(value)}
          onBlur={(event, editor) => !disabled && onBlur?.(editor?.getValue() || '')}
          name="code-editor"
          //hack for local machine
          editorProps={{}}
          readOnly={disabled}
        />
      </div>
      <FieldErrorAndDescription error={error} />
      {!isValid && (
        <FieldErrorAndDescription error={t('dashboard.code_editor.code_is_not_valid')} />
      )}
    </div>
  )
}

export default CodeEditor
